import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Logout as LogoutIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

const businessTypes = [
  'Retail Store',
  'Wholesale Business',
  'Manufacturing',
  'Service Provider',
  'Restaurant/Food Service',
  'Healthcare',
  'Education',
  'Technology/IT',
  'Construction',
  'Transportation',
  'Real Estate',
  'Other'
];

const normalizeAccessRole = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole === 'owner' || normalizedRole === 'admin') {
    return 'admin';
  }
  if (normalizedRole === 'staff' || normalizedRole === 'viewer') {
    return 'staff';
  }
  return 'admin';
};

function Profile({ user, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [avatarDialog, setAvatarDialog] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [personalData, setPersonalData] = useState({
    name: '',
    phone: '',
  });
  const accessRole = normalizeAccessRole(user?.accessRole);
  const isStaffUser = accessRole === 'staff' || Boolean(user?.isStaff);

  const [profileData, setProfileData] = useState({
    businessId: '',
    businessName: '',
    businessType: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    phone: '',
    email: '',
    website: '',
    description: '',
    employeeCount: '',
    foundedYear: '',
  });

  const loadPersonalData = () => {
    let storedProfile = {};
    try {
      storedProfile = JSON.parse(localStorage.getItem(`profile_${user.id}`) || '{}');
    } catch (parseError) {
      console.warn('Unable to parse stored personal profile:', parseError);
    }

    setPersonalData({
      name: user?.name || storedProfile?.name || '',
      phone: storedProfile?.phone || user?.phone || '',
    });
  };

  // Load profile data on component mount
  useEffect(() => {
    loadProfileData();
  }, [user.id]);

  useEffect(() => {
    loadPersonalData();
  }, [user.id, user.name, user.phone]);

  // Debug: Log when user prop changes
  useEffect(() => {
    console.log('🔄 Profile component received updated user:', user);
    console.log('🔄 User business data:', user?.business);
    console.log('🔄 User business name:', user?.business?.name);
    console.log('🔄 User imageUrl:', user.imageUrl);
  }, [user]);

  // Load user profile data from database
  const loadUserProfileData = async () => {
    try {
      console.log('🔍 Loading business profile data from database...');

      // Import user management API
      const { userManagementApi } = await import('../shared/api/userApi');

      // Get business profile from database using user ID (not email)
      const businessResponse = await userManagementApi.getBusinessProfile(user.id);

      if (businessResponse.success && businessResponse.business) {
        const business = businessResponse.business;
        console.log('✅ Found business profile in database:', business);

        // Update form with existing business profile data
        setProfileData({
          businessId: business.id || '',
          businessName: business.name || '',
          businessType: business.type || business.business_type || '',
          industry: business.industry || '',
          phone: business.phone || '',
          email: business.email || user.email || '',
          website: business.website || '',
          address: business.address || '',
          city: business.city || '',
          state: business.state || '',
          zipCode: business.zipCode || business.zip_code || '',
          country: business.country || 'India',
          description: business.description || '',
          employeeCount: business.employeeCount || business.employee_count || '',
          foundedYear: business.foundedYear || business.founded_year || '',
        });

        setProfileExists(true);
        console.log('✅ Business profile data loaded from database');
      } else {
        // No business data found in database, use data from user.business as fallback
        console.log('ℹ️ No business profile data found in database, using user.business data as fallback');
        setProfileData(prev => ({
          ...prev,
          businessId: user.business?.id || prev.businessId,
          businessName: user.business?.name || prev.businessName,
          businessType: user.business?.type || prev.businessType,
          industry: user.business?.industry || prev.industry,
          phone: user.business?.phone || prev.phone,
          email: user.business?.email || user.email || prev.email,
          website: user.business?.website || prev.website,
          address: user.business?.address || prev.address,
          city: user.business?.city || prev.city,
          state: user.business?.state || prev.state,
          zipCode: user.business?.zipCode || prev.zipCode,
          country: user.business?.country || prev.country,
          description: user.business?.description || prev.description,
          employeeCount: user.business?.employeeCount || prev.employeeCount,
          foundedYear: user.business?.foundedYear || prev.foundedYear,
        }));
        setProfileExists(false);
      }
    } catch (error) {
      console.warn('Failed to load business profile data:', error.message);
      setProfileExists(false);
    }
  };

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, initialize from user.business data if available
      if (user.business) {
        console.log('🔄 Initializing profile from user.business data:', user.business);
        setProfileData({
          businessId: user.business.id || '',
          businessName: user.business.name || '',
          businessType: user.business.type || '',
          industry: user.business.industry || '',
          address: user.business.address || '',
          city: user.business.city || '',
          state: user.business.state || '',
          zipCode: user.business.zipCode || '',
          country: user.business.country || 'India',
          phone: user.business.phone || '',
          email: user.business.email || user.email || '',
          website: user.business.website || '',
          description: user.business.description || '',
          employeeCount: user.business.employeeCount || '',
          foundedYear: user.business.foundedYear || '',
        });
      }

      // Staff users can view business context but cannot query/update business profile endpoints.
      if (!isStaffUser) {
        await loadUserProfileData();
      }

    } catch (error) {
      setError('Failed to load profile data');
      console.error('Profile load error:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const handlePersonalInputChange = (field, value) => {
    setPersonalData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const handleSavePersonal = async () => {
    const trimmedName = (personalData.name || '').trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { userManagementApi } = await import('../shared/api/userApi');
      const profilePayload = {
        name: trimmedName,
        email: user.email || '',
        phone: (personalData.phone || '').trim(),
      };

      try {
        await userManagementApi.updateUserProfile(user.id, profilePayload);
      } catch (profileSyncError) {
        console.warn('Failed to sync personal profile to backend. Using local fallback.', profileSyncError);
      }

      let existingStoredProfile = {};
      try {
        existingStoredProfile = JSON.parse(localStorage.getItem(`profile_${user.id}`) || '{}');
      } catch (parseError) {
        console.warn('Unable to parse existing personal profile before save:', parseError);
      }

      const updatedUser = {
        ...user,
        name: trimmedName,
        phone: profilePayload.phone,
      };

      localStorage.setItem('userData', JSON.stringify(updatedUser));
      localStorage.setItem(`profile_${user.id}`, JSON.stringify({
        ...existingStoredProfile,
        ...profilePayload,
        userId: user.id,
        updatedAt: new Date().toISOString(),
      }));

      onUpdate(updatedUser);
      setSuccess('Personal details updated successfully!');
      setEditMode(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update personal details. Please try again.');
      console.error('Personal profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!profileData.businessName || !profileData.businessType) {
      setError('Business name and type are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      console.log('💾 Saving business profile to database...');

      // Import user management API
      const { userManagementApi } = await import('../shared/api/userApi');

      // Prepare business data for database
      const businessData = {
        name: profileData.businessName,
        type: profileData.businessType,
        industry: profileData.industry,
        address: profileData.address,
        city: profileData.city,
        state: profileData.state,
        zipCode: profileData.zipCode,
        country: profileData.country,
        phone: profileData.phone,
        website: profileData.website,
        description: profileData.description,
        employeeCount: profileData.employeeCount,
        foundedYear: profileData.foundedYear,
      };

      // Save to database
      const updateResponse = await userManagementApi.updateBusinessProfile(user.id, businessData);

      if (!updateResponse.success) {
        throw new Error(updateResponse.message || 'Failed to update business profile');
      }

      const updatedBusiness = updateResponse.business;

      console.log('✅ Business profile updated in database:', updatedBusiness);

      // Update localStorage
      localStorage.setItem(`business_${user.id}`, JSON.stringify(updatedBusiness));

      // Update userData in localStorage to include updated business data
      const updatedUser = {
        ...user,
        business: updatedBusiness,
      };
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      console.log('🔄 Profile updated - new business name:', updatedBusiness.name);
      console.log('🔄 Updated user object:', updatedUser);
      console.log('💾 Updated localStorage with new business data');

      onUpdate(updatedUser);
      setProfileData(prev => ({
        ...prev,
        businessId: updatedBusiness.id || prev.businessId,
      }));
      setSuccess('Profile updated successfully!');
      setEditMode(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };


  const handleLogout = () => {
    // Clear all stored data
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    localStorage.removeItem(`business_${user.id}`);
    localStorage.removeItem(`settings_${user.id}`);
    localStorage.removeItem(`profile_${user.id}`);

    // Clear any other user-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.includes(user.id)) {
        localStorage.removeItem(key);
      }
    });

    // Redirect to login or reload the page
    window.location.href = '/';
  };

  const handleCancel = () => {
    if (isStaffUser) {
      loadPersonalData();
    } else {
      // Reload data from database to reset form
      loadProfileData();
    }
    setEditMode(false);
    setError(null);
  };

  const handleCopyBusinessId = async () => {
    const businessIdValue = String(profileData.businessId || user?.business?.id || '').trim();
    if (!businessIdValue) {
      setError('Business ID is not available yet.');
      return;
    }

    try {
      await navigator.clipboard.writeText(businessIdValue);
      setSuccess('Business ID copied!');
      setTimeout(() => setSuccess(null), 2500);
    } catch (copyError) {
      console.error('Copy Business ID failed:', copyError);
      setError('Failed to copy Business ID.');
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      try {
        setUploading(true);
        setError(null);

        console.log('📤 Starting image upload process...');

        // Import Google Sheets API and ensure access token is set
        const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');

        // Ensure access token is set (get it from user object or localStorage)
        const accessToken = user.accessToken || localStorage.getItem('authToken');
        if (accessToken && accessToken !== 'authenticated') {
          googleSheetsAPI.setAccessToken(accessToken);
          console.log('🔑 Access token set for image upload');
        } else {
          throw new Error('No valid access token available for image upload');
        }

        // Upload image to Google Drive
        const uploadResult = await googleSheetsAPI.uploadImageToDrive(file, user.id);

        if (uploadResult.success) {
          console.log('✅ Image uploaded successfully:', uploadResult.shareableLink);

          // Delete old profile image if it exists (optional cleanup)
          if (user.imageUrl && user.imageUrl !== uploadResult.shareableLink) {
            await googleSheetsAPI.deleteOldProfileImage(user.imageUrl);
          }

          // Store base64 data in localStorage for CORS-free display
          if (uploadResult.base64Data) {
            localStorage.setItem(`profile_image_${user.id}`, uploadResult.base64Data);
            console.log('💾 Base64 image data stored in localStorage');
          }

          // Update user profile with new image URL and base64 data
          const updatedUser = {
            ...user,
            imageUrl: uploadResult.shareableLink,
            imageBase64: uploadResult.base64Data // Store base64 for display
          };

          console.log('👤 Updating user with new image URL:', uploadResult.shareableLink);
          console.log('👤 Current user object:', user);
          console.log('👤 Updated user object:', updatedUser);

          // Update parent component
          onUpdate(updatedUser);

          // Update localStorage
          localStorage.setItem(`user_${user.id}`, JSON.stringify(updatedUser));

          // Force immediate re-render by updating a local state
          setProfileData(prev => ({ ...prev }));

          // Force re-render by updating local state
          setProfileData(prev => ({
            ...prev,
            // This will trigger a re-render of the Avatar component
          }));

          setSuccess('Profile picture updated successfully!');
          setAvatarDialog(false);

          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);

        } else {
          throw new Error('Upload failed');
        }

      } catch (error) {
        console.error('❌ Image upload error:', error);
        setError(`Failed to upload image: ${error.message}`);
      } finally {
        setUploading(false);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>

      {/* Success/Error Messages */}
      {(error || success) && (
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
        <Grid container spacing={4}>
          {/* Card 1: Profile + Business Overview */}
          <Grid item xs={12} md={isStaffUser ? 12 : 4}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              height: '100%',
              width: '103%',
              minHeight: 270,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: 4, flex: 1 }}>
                <Grid container spacing={15} alignItems="flex-start">
                  {/* Profile Section - Left Side */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                        <Avatar
                            src={user.imageBase64 || localStorage.getItem(`profile_image_${user.id}`) || user.imageUrl || ''}
                            alt={user.name}
                            key={user.imageBase64 || user.imageUrl || 'default'} // Force re-render when image data changes
                            sx={{
                              width: 100,
                              height: 100,
                              mx: 'auto',
                              border: '4px solid #667eea',
                              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                              },
                              // Show user initials as fallback
                              backgroundColor: '#667eea',
                              color: 'white',
                              fontSize: '2rem',
                              fontWeight: 700
                            }}
                            onClick={() => setAvatarDialog(true)}
                            onError={(e) => {
                              console.log('❌ Avatar image failed to load:', e.target.src);

                              // Try to get base64 from localStorage if not already using it
                              const storedBase64 = localStorage.getItem(`profile_image_${user.id}`);
                              if (storedBase64 && e.target.src !== storedBase64) {
                                console.log('🔄 Trying stored base64 data...');
                                e.target.src = storedBase64;
                                return;
                              }

                              // If we have a Google profile image URL that's failing, try to get a fresh one
                              if (user.imageUrl && e.target.src === user.imageUrl) {
                                console.log('🔄 Google profile image failed, setting transparent fallback...');
                                // Set a transparent data URL to prevent further network requests
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
                                return;
                              }

                              // Try fallback to direct link if thumbnail fails
                              if (user.imageUrl && user.imageUrl.includes('thumbnail')) {
                                console.log('🔄 Trying fallback direct link...');
                                const fileId = user.imageUrl.split('id=')[1]?.split('&')[0];
                                if (fileId) {
                                  const fallbackUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
                                  console.log('🔄 Fallback URL:', fallbackUrl);
                                  e.target.src = fallbackUrl;
                                }
                              }

                              // If all else fails, show initials
                              console.log('❌ All avatar sources failed, showing initials');
                              e.target.style.display = 'none';
                              // The Avatar component will show the children (initials) when src fails
                            }}
                            imgProps={{
                              onLoad: () => console.log('✅ Avatar image loaded successfully'),
                              onError: () => console.log('❌ Avatar image load error')
                            }}
                          >
                            {/* Show user initials as fallback */}
                            {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                          </Avatar>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            }
                          }}
                          onClick={() => setAvatarDialog(true)}
                        >
                          <PhotoCameraIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      </Box>

                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                        {user.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {user.email}
                      </Typography>
                      <Chip
                        label="Active Account"
                        sx={{
                          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                          color: 'white',
                          fontWeight: 600,
                          px: 2,
                          py: 1,
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Business Overview Section - Middle */}
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333' }}>
                        Business Overview
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Business Name:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {profileData.businessName || 'Not set'}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Business ID:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#667eea' }}>
                            {profileData.businessId || user?.business?.id || 'Not set'}
                          </Typography>
                          <Tooltip title="Copy Business ID">
                            <span>
                              <IconButton
                                size="small"
                                onClick={handleCopyBusinessId}
                                disabled={!(profileData.businessId || user?.business?.id)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Type:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {profileData.businessType || 'Not set'}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Industry:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {profileData.industry || 'Not set'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {profileData.employeeCount && (
                          <Chip label={`${profileData.employeeCount} employees`} size="small" variant="outlined" />
                        )}
                        {profileData.foundedYear && (
                          <Chip label={`Founded ${profileData.foundedYear}`} size="small" variant="outlined" />
                        )}
                      </Box>

                      {profileData.description && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Description:
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                            {profileData.description}
                          </Typography>
                        </Box>
                      )}

                      {profileData.website && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Website:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#667eea' }}>
                            {profileData.website}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Action Buttons - Right Side */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginLeft: -9, height: '100%', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditMode(true)}
                        sx={{
                          borderRadius: 2,
                          borderColor: '#667eea',
                          color: '#667eea',
                          marginLeft: 33,
                          marginTop: 7,
                          py: 1.5,
                          '&:hover': {
                            borderColor: '#5a67d8',
                            color: '#5a67d8',
                            background: 'rgba(102, 126, 234, 0.04)',
                          }
                        }}
                      >
                        {isStaffUser ? 'Edit Personal Details' : 'Edit Profile'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                        sx={{
                          borderRadius: 2,
                          borderColor: '#ef4444',
                          color: '#ef4444',
                          marginLeft: 33,
                          py: 1.5,
                          '&:hover': {
                            borderColor: '#dc2626',
                            color: '#dc2626',
                            background: 'rgba(239, 68, 68, 0.04)',
                          }
                        }}
                      >
                        Logout
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {!isStaffUser && (
          <>
          {/* Card 2: Business Information */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: 4, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <BusinessIcon sx={{ color: '#667eea', mr: 2, fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                    Business Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Name"
                      value={profileData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      required
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required disabled={!editMode}>
                      <InputLabel>Business Type</InputLabel>
                      <Select
                        value={profileData.businessType}
                        onChange={(e) => handleInputChange('businessType', e.target.value)}
                        label="Business Type"
                        sx={{
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }}
                      >
                        {businessTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Industry/Sector"
                      value={profileData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      placeholder="e.g., Electronics, Fashion"
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={profileData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://www.yourbusiness.com"
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employee Count"
                      value={profileData.employeeCount}
                      onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                      placeholder="e.g., 1-10"
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Founded Year"
                      value={profileData.foundedYear}
                      onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                      placeholder="e.g., 2020"
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Description"
                      value={profileData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      multiline
                      rows={3}
                      placeholder="Tell us about your business..."
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3: Contact and Address */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CardContent sx={{ p: 4, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PhoneIcon sx={{ color: '#10b981', mr: 2, fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                    Contact & Address
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={profileData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+91 9876543210"
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address (Managed by Google)"
                      value={profileData.email}
                      placeholder="business@example.com"
                      size="small"
                      variant="outlined"
                      disabled={true}
                      type="email"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: '#f5f5f5',
                          opacity: 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={profileData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={profileData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State/Province"
                      value={profileData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP/Postal Code"
                      value={profileData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={profileData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      size="small"
                      variant="outlined"
                      disabled={!editMode}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: editMode ? 'white' : '#f5f5f5',
                          opacity: editMode ? 1 : 0.6,
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          </>
          )}

          {isStaffUser && (
            <Grid item xs={12}>
              <Card sx={{
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PersonIcon sx={{ color: '#667eea', mr: 2, fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                      Personal Details
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Staff access allows editing only personal information.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={personalData.name}
                        onChange={(e) => handlePersonalInputChange('name', e.target.value)}
                        size="small"
                        variant="outlined"
                        disabled={!editMode}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: editMode ? 'white' : '#f5f5f5',
                            opacity: editMode ? 1 : 0.6,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={personalData.phone}
                        onChange={(e) => handlePersonalInputChange('phone', e.target.value)}
                        placeholder="+91 9876543210"
                        size="small"
                        variant="outlined"
                        disabled={!editMode}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: editMode ? 'white' : '#f5f5f5',
                            opacity: editMode ? 1 : 0.6,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        value={user.email || ''}
                        size="small"
                        variant="outlined"
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: '#f5f5f5',
                            opacity: 0.6,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Action Buttons - Only show when in edit mode */}
        {editMode && (
          <Box sx={{ mt: 4, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={isStaffUser ? handleSavePersonal : handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                minWidth: 160,
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                }
              }}
            >
              {saving ? 'Saving...' : (isStaffUser ? 'Save Personal Details' : 'Save Profile')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              startIcon={<CancelIcon />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: '#64748b',
                color: '#334155',
                minWidth: 160,
                '&:hover': {
                  borderColor: '#475569',
                  color: '#1e293b',
                  background: 'rgba(51, 65, 85, 0.04)',
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        )}

      </Box>

      {/* Avatar Upload Dialog */}
      <Dialog open={avatarDialog} onClose={() => setAvatarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#333' }}>
          {uploading ? 'Uploading Image...' : 'Update Profile Picture'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a new profile picture. Supported formats: JPG, PNG, GIF (max 5MB)
          </Typography>
          <input
            accept="image/*"
            type="file"
            onChange={handleAvatarUpload}
            style={{ display: 'none' }}
            id="avatar-upload"
          />
          <label htmlFor="avatar-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              disabled={uploading}
              sx={{
                borderRadius: 2,
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5a67d8',
                  color: '#5a67d8',
                  background: 'rgba(102, 126, 234, 0.04)',
                },
                '&.Mui-disabled': {
                  borderColor: '#ccc',
                  color: '#ccc',
                }
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Uploading...
                </>
              ) : (
                'Choose File'
              )}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAvatarDialog(false)}
            disabled={uploading}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: '#667eea',
              '&.Mui-disabled': {
                color: '#ccc',
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;
